import { useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    FILTER_JOB_KEY,
    FILTER_LOCATION_KEY,
    FILTER_MANAGER_KEY,
    FILTER_PRIMARY_JOB_KEY,
    FILTER_SHIFT_PATTERN_KEY,
    FILTER_WORK_CENTERS_KEY,
} from "../../../assets/constants";
import {FILTER_WORKER_TYPE_KEY} from "../../../assets/constants";
import {
    JOB_ROLES,
    LOCATIONS,
    PRIMARY_JOB_ROLES,
    REPORTING_TO,
    SHIFT_PATTERNS,
    WORK_CENTERS,
} from "../../filters/constants";
import {WORKER_TYPES} from "../../filters/constants";
import { AppliedFilter, DataRelation, FilterContext } from "../../filters/filter-context";
import {
    getJobsMatrixDefaultFilters,
    getJobsMatrixFiltersForUser,
} from "../store/jobs-matrix-slice";

export const useJobsMatrixFilters = () => {
    const dispatch = useDispatch();

    const {
        filterData,
        getJobsMatrixFiltersForUserStatus,
        isWorkerSubTypeEnabled,
        defaultFiltersData,
    } = useSelector((state: any) => ({
        filterData: state.jobsMatrix?.filterData || {},
        getJobsMatrixFiltersForUserStatus:
            state.jobsMatrix?.getJobsMatrixFiltersForUser?.status || "idle",
        isWorkerSubTypeEnabled: state.user?.userData?.workerSubTypeEnabled || false,
        defaultFiltersData: state.jobsMatrix?.defaultFiltersData ?? null,
    }));

    const filterContext = useContext(FilterContext);
    if (!filterContext) {
        throw new Error("useJobsMatrixFilters must be used within a FilterProvider");
    }
    const { appliedFilters, setAppliedFilters, open: isFilterOpen, setFilterOpen, setFilters, filters, setDataRelations, filterAccordionHeight, setIsLoading } = filterContext;

    // Fetch filters and default filters on mount
    useEffect(() => {
        setIsLoading?.(true);
        dispatch(getJobsMatrixFiltersForUser());
        dispatch(getJobsMatrixDefaultFilters());
    }, [dispatch, setIsLoading]);

    // Setup filters from API response
    useEffect(() => {
        if (getJobsMatrixFiltersForUserStatus === "fulfilled") {
            setIsLoading?.(false);
            try {
                const {
                    allLocations,
                    allWorkcenters,
                    allManagers,
                    allJobRoles,
                    workerTypes,
                    shiftPatterns,
                    workerSubTypes,
                    filterRelationships,
                } = filterData;

                const filters: any[] = [];

                // Locations
                if (allLocations?.length > 0) {
                    filters.push({
                        id: 1,
                        name: LOCATIONS,
                        key: FILTER_LOCATION_KEY,
                        data: allLocations,
                        reducedData: allLocations,
                        searchedData: allLocations,
                    });
                }

                // Work Centers
                if (allWorkcenters?.length > 0) {
                    filters.push({
                        id: 2,
                        name: WORK_CENTERS,
                        key: FILTER_WORK_CENTERS_KEY,
                        data: allWorkcenters,
                        reducedData: allWorkcenters,
                        searchedData: allWorkcenters,
                    });
                }

                // Primary Job Roles
                if (allJobRoles?.length > 0) {
                    filters.push({
                        id: 3,
                        name: PRIMARY_JOB_ROLES,
                        key: FILTER_PRIMARY_JOB_KEY,
                        data: allJobRoles,
                        reducedData: allJobRoles,
                        searchedData: allJobRoles,
                    });
                }

                // Job Roles
                if (allJobRoles?.length > 0) {
                    filters.push({
                        id: 4,
                        name: JOB_ROLES,
                        key: FILTER_JOB_KEY,
                        data: allJobRoles,
                        reducedData: allJobRoles,
                        searchedData: allJobRoles,
                    });
                }

                // Worker Types (with subtypes if enabled)
                if (workerTypes?.length > 0) {
                    const workerTypeData: Array<{
                        id: number;
                        name: string;
                        parentId?: number;
                    }> = [];

                    // Add main worker types
                    workerTypes.forEach((type: any) => {
                        workerTypeData.push({
                            id: parseInt(type.id.toString()),
                            name: type.name,
                        });
                    });

                    // Add subtypes with offset if enabled
                    if (workerSubTypes?.length > 0 && isWorkerSubTypeEnabled) {
                        workerSubTypes.forEach((subType: any) => {
                            workerTypeData.push({
                                id: parseInt(subType.id.toString()) + 1000,
                                name: subType.name,
                                parentId: parseInt(subType.workerTypeId.toString()),
                            });
                        });
                    }

                    filters.push({
                        id: 5,
                        name: WORKER_TYPES,
                        key: FILTER_WORKER_TYPE_KEY,
                        data: workerTypeData,
                        reducedData: workerTypeData,
                        searchedData: workerTypeData,
                    });
                }


                // Reporting Managers
                if (allManagers?.length > 0) {
                    filters.push({
                        id: 6,
                        name: REPORTING_TO,
                        key: FILTER_MANAGER_KEY,
                        data: allManagers,
                        reducedData: allManagers,
                        searchedData: allManagers,
                    });
                }

                // Shift Patterns
                if (shiftPatterns?.length > 0) {
                    filters.push({
                        id: 7,
                        name: SHIFT_PATTERNS,
                        key: FILTER_SHIFT_PATTERN_KEY,
                        data: shiftPatterns,
                        reducedData: shiftPatterns,
                        searchedData: shiftPatterns,
                    });
                }

                setFilters(filters);
              // Clone DataRelations and extend for shift equivalents
              let relations: DataRelation[] = filterRelationships ?? [];

              const workCenterToJob = relations.find(
                r => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_JOB_KEY
              );

              if (workCenterToJob) {
                const primaryJobRelation: DataRelation = {
                  from: FILTER_WORK_CENTERS_KEY,
                  to: FILTER_PRIMARY_JOB_KEY,
                  mappings: workCenterToJob.mappings,
                }
                relations = [...relations, primaryJobRelation];
              }
              setDataRelations(relations);
            } catch (e) {
                console.error("Error setting up filters:", e);
            }
        }
    }, [filterData, getJobsMatrixFiltersForUserStatus, isWorkerSubTypeEnabled, setDataRelations, setFilters, setIsLoading]);

    const hasAppliedDefaults = useRef(false);

    // Build defaultAppliedFilters from the default filters API response + filter data
    const defaultAppliedFilters = useMemo((): AppliedFilter[] => {
        if (!defaultFiltersData || getJobsMatrixFiltersForUserStatus !== "fulfilled") return [];

        const result: AppliedFilter[] = [];
        const now = Date.now();

        const parseIds = (str?: string): number[] =>
            str ? str.split(",").map((id) => parseInt(id.trim(), 10)).filter(Boolean) : [];

        // Locations
        const locationIds = parseIds(defaultFiltersData.locations);
        if (locationIds.length > 0) {
            const data = locationIds.reduce((acc: any[], id) => {
                const loc = filterData.allLocations?.find((l: any) => l.id === id);
                if (loc) acc.push({ id: loc.id, name: loc.name });
                return acc;
            }, []);
            if (data.length > 0) result.push({ type: LOCATIONS, key: FILTER_LOCATION_KEY, timeAdded: now, data });
        }

        // Work Centers
        const wcIds = parseIds(defaultFiltersData.workcenters);
        if (wcIds.length > 0) {
            const data = wcIds.reduce((acc: any[], id) => {
                const wc = filterData.allWorkcenters?.find((w: any) => w.id === id);
                if (wc) acc.push({ id: wc.id, name: wc.name });
                return acc;
            }, []);
            if (data.length > 0) result.push({ type: WORK_CENTERS, key: FILTER_WORK_CENTERS_KEY, timeAdded: now + 1, data });
        }

        // Managers
        const managerIds = parseIds(defaultFiltersData.managers);
        if (managerIds.length > 0) {
            const data = managerIds.reduce((acc: any[], id) => {
                const mgr = filterData.allManagers?.find((m: any) => m.id === id);
                if (mgr) acc.push({ id: mgr.id, name: mgr.name });
                return acc;
            }, []);
            if (data.length > 0) result.push({ type: REPORTING_TO, key: FILTER_MANAGER_KEY, timeAdded: now + 2, data });
        }

        // Shift Patterns
        const spIds = parseIds(defaultFiltersData.shiftPatterns);
        if (spIds.length > 0) {
            const data = spIds.reduce((acc: any[], id) => {
                const sp = filterData.shiftPatterns?.find((s: any) => s.id === id);
                if (sp) acc.push({ id: sp.id, name: sp.name });
                return acc;
            }, []);
            if (data.length > 0) result.push({ type: SHIFT_PATTERNS, key: FILTER_SHIFT_PATTERN_KEY, timeAdded: now + 3, data });
        }

        return result;
    }, [defaultFiltersData, filterData, getJobsMatrixFiltersForUserStatus]);

    // Apply default filters once when computed, if no filters are currently active
    useEffect(() => {
        if (!hasAppliedDefaults.current && defaultAppliedFilters.length > 0) {
            if (appliedFilters.length === 0) {
                setAppliedFilters(defaultAppliedFilters);
            }
            hasAppliedDefaults.current = true;
        }
    }, [defaultAppliedFilters, appliedFilters.length, setAppliedFilters]);

    return {
        filters,
        setFilters,
        appliedFilters,
        isFilterOpen,
        setIsFilterOpen: setFilterOpen,
        filterStatus: getJobsMatrixFiltersForUserStatus,
        filterAccordionHeight,
        defaultAppliedFilters,
    };
};
